import { useMemo } from 'react'
import { useAuth } from '@frontend/contexts/AuthContext'
import {
  hasPermission,
  hasMinRole,
  canModifyUserRole,
  getAssignableRolesForActor,
  type Permission,
  type UserRole,
  type AssignableRole,
} from '@common/permissions'

/**
 * Hook for checking user permissions in components.
 * Uses the current user's role from AuthContext.
 */
export function usePermissions() {
  const { user, loading } = useAuth()

  return useMemo(
    () => ({
      /**
       * Check if current user has a specific permission.
       */
      can: (permission: Permission): boolean => {
        if (!user?.role) return false
        return hasPermission(user.role as UserRole, permission)
      },

      /**
       * Check if current user meets or exceeds a minimum role level.
       */
      isAtLeast: (role: UserRole): boolean => {
        if (!user?.role) return false
        return hasMinRole(user.role as UserRole, role)
      },

      /**
       * Check if current user can modify a target user's role.
       */
      canManageUser: (targetRole: UserRole): boolean => {
        if (!user?.role) return false
        return canModifyUserRole(user.role as UserRole, targetRole)
      },

      /**
       * Get the list of roles the current user can assign.
       */
      getAssignableRoles: (): AssignableRole[] => {
        if (!user?.role) return []
        return getAssignableRolesForActor(user.role as UserRole)
      },

      /**
       * Current user's role (for display purposes).
       */
      role: user?.role as UserRole | undefined,

      /**
       * Whether auth is still loading.
       */
      isLoading: loading,
    }),
    [user, loading]
  )
}
